import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-apollo';
import gql from 'graphql-tag';
import StudentFormFields from './StudentFormFields';
import StudentFormSuccess from './StudentFormSuccess';
import './_admissionForms.scss';

// ── GraphQL Queries & Mutations ──────
const GET_PUBLIC_FORM = gql`
  query ($form_id: Int!) {
    getEnquiryFormFields(form_id: $form_id) {
      id
      field_name
      field_type
      is_required
      options
      order_no
    }
  }
`;

const GET_ENQUIRY_FORM_DETAILS = gql`
  query ($institute_id: Int!, $branch_id: String!) {
    getEnquiryForms(institute_id: $institute_id, branch_id: $branch_id) {
      id
      form_name
      form_url
      form_field_ids
    }
  }
`;

const SUBMIT_ENQUIRY_FORM = gql`
  mutation ($input: SubmitEnquiryFormInput!) {
    submitEnquiryForm(input: $input)
  }
`;
// ─────────────────────────────────────

const StudentFormPage = () => {
  const { form_id } = useParams();

  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [fields, setFields] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [formData, setFormData] = useState({
    student_name: "",
    student_mobile: "",
    student_email: "",
    gender: "",
    dob: "",
    enquiry_course_id: "",
    field_values: [],
  });

  // Load form fields from GraphQL
  const { data, loading, error } = useQuery(GET_PUBLIC_FORM, {
    variables: { form_id: parseInt(form_id) || 0 },
    fetchPolicy: "network-only",
  });

  const [submitForm, { loading: submitting }] = useMutation(SUBMIT_ENQUIRY_FORM);

  useEffect(() => {
    if (data && data.getEnquiryFormFields) {
      const allFields = data.getEnquiryFormFields;
      console.log("Public Form Fields:", allFields);

      // Extract Course Name options and filter out mandatory fields from the dynamic component
      const standardLabels = ["name", "mobile", "mobile number", "email", "date of birth", "dob", "gender", "sex"];
      
      const dynamic = allFields.filter((f) => {
        const label = (f.field_name || "").toLowerCase();
        
        if (label === "course name") {
          setCourseOptions(f.options || []);
          return false;
        }

        const isBasic = f.id < 0 || standardLabels.includes(label);
        return !isBasic;
      });

      setFields(dynamic);
    }
  }, [data]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    // Basic Validation
    if (!formData.student_name.trim()) return setErrorMsg("Name is required");
    if (!formData.student_mobile.trim()) return setErrorMsg("Mobile is required");
    if (formData.student_mobile.length !== 10) return setErrorMsg("Enter valid 10 digit mobile");
    if (!formData.dob) return setErrorMsg("Date of Birth is required");
    if (!formData.gender) return setErrorMsg("Gender is required");
    if (!formData.enquiry_course_id) return setErrorMsg("Please select a Course");

    try {
      const remainingFormData = formData.field_values.map(fv => ({
        enquiry_field_id: fv.field_id,
        value: fv.field_value.toString()
      }));

      await submitForm({
        variables: {
          input: {
            enquiry_form_id: parseInt(form_id),
            student_name: formData.student_name.trim(),
            student_mobile: formData.student_mobile.trim(),
            student_email: formData.student_email.trim(),
            gender: formData.gender,
            dob: formData.dob,
            enquiry_course_id: parseInt(formData.enquiry_course_id),
            remainingFormData,
          }
        }
      });

      setSubmitted(true);
    } catch (err) {
      setErrorMsg(err.message.replace("GraphQL error: ", ""));
    }
  };

  if (loading) {
    return (
      <div className="student-form-wrapper">
        <div className="student-form-card">
          <div className="form-header">
            <img src="/images/rizeelogo.png" alt="logo" className="form-logo" />
            <p>Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="student-form-wrapper">
        <div className="student-form-card">
          <div className="form-header">
            <img src="/images/rizeelogo.png" alt="logo" className="form-logo" />
            <div className="alert alert-danger">Error loading form. Please refresh.</div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return <StudentFormSuccess name={formData.student_name} />;
  }

  return (
    <div className="student-form-wrapper">
      <div className="student-form-card">
        <div className="form-header">
          <img src="/images/rizeelogo.png" alt="logo" className="form-logo" />
          <h2>New Registration</h2>
          <p>Fill in your details below to register</p>
        </div>

        <form onSubmit={handleSubmit} className="student-form">
          {/* Fixed Fields */}
          <div className="form-group">
            <label>Full Name <span className="req">*</span></label>
            <input
              type="text"
              name="student_name"
              className="form-control"
              placeholder="Enter your full name"
              value={formData.student_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="row">
            <div className="col-md-6 px-2">
              <div className="form-group">
                <label>Mobile Number <span className="req">*</span></label>
                <input
                  type="tel"
                  name="student_mobile"
                  className="form-control"
                  placeholder="10 digit mobile"
                  value={formData.student_mobile}
                  onChange={handleChange}
                  maxLength={10}
                  required
                />
              </div>
            </div>
            <div className="col-md-6 px-2">
              <div className="form-group">
                <label>Email <span className="opt">(optional)</span></label>
                <input
                  type="email"
                  name="student_email"
                  className="form-control"
                  placeholder="Email address"
                  value={formData.student_email}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 px-2">
              <div className="form-group">
                <label>Date of Birth <span className="req">*</span></label>
                <input
                  type="date"
                  name="dob"
                  className="form-control"
                  value={formData.dob}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="col-md-6 px-2">
              <div className="form-group">
                <label>Gender <span className="req">*</span></label>
                <select 
                  name="gender" 
                  className="form-control" 
                  value={formData.gender} 
                  onChange={handleChange} 
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Others">Others</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Course Name <span className="req">*</span></label>
            <select
              name="enquiry_course_id"
              className="form-control"
              value={formData.enquiry_course_id}
              onChange={handleChange}
              required
            >
              <option value="">Select Course</option>
              {courseOptions.map((course) => (
                <option key={course.value} value={course.value}>
                  {course.label}
                </option>
              ))}
            </select>
          </div>

          {/* Dynamic Custom Fields */}
          {fields && fields.length > 0 && (
            <StudentFormFields
              fields={fields}
              onChange={(field_values) =>
                setFormData({ ...formData, field_values })
              }
            />
          )}

          {errorMsg && <div className="alert alert-danger py-2">{errorMsg}</div>}

          <button
            type="submit"
            className="btn btn-primary btn-block sf-submit-btn"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" />
                Submitting...
              </>
            ) : "Submit Form"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentFormPage;
